import { accessTokens } from '../api/accessTokenMgmt'
import { requireClientSigningFeature } from './clientSigningFeature'
import {
    commitRecoveredWalletForDevice,
    getClientShare,
    getWalletIdentityInfo,
    prepareRecoveredWalletForDevice,
    recoverLocalWalletTextWithShares,
    signWalletRecoveryProof,
    walletRootIdentityPublicKey,
} from './bsv-signing'
import {
    createWalletPasskeyEnvelope,
    openWalletPasskeyEnvelope,
    type WalletPasskeyEnvelope,
    type WalletRecoveryIdentity,
} from './walletPasskeyCrypto'
import {
    createWalletPasskey,
    useWalletPasskey,
    type PasskeyCreationOptionsJSON,
    type PasskeyRequestOptionsJSON,
} from './walletPasskeyWebAuthn'

export interface WalletPasskeyProvider {
    name: string
    ecosystem: 'apple' | 'google' | null
}
export interface WalletPasskeySummary {
    id: string
    credential_id: string
    verified: boolean
    envelope_present: boolean
    created_at: string
    provider?: WalletPasskeyProvider | null
    root_identity_key?: string | null
}
export interface WalletPasskeyStatus {
    identity: WalletRecoveryIdentity
    root_identity_key?: string | null
    passkeys: WalletPasskeySummary[]
}
interface EnrollmentOptions {
    ceremony_id: string
    public_key: PasskeyCreationOptionsJSON
    identity: WalletRecoveryIdentity
    prf_salt: string
}
interface AssertionOptions {
    ceremony_id: string
    public_key: PasskeyRequestOptionsJSON
}
interface AssertionGrant {
    grant_id: string
    envelope: WalletPasskeyEnvelope | null
    proof_message: string | null
    identity: WalletRecoveryIdentity
}
export type WalletPasskeyStep =
    | { kind: 'create'; options: EnrollmentOptions; walletText: string }
    | {
          kind: 'encrypt'
          options: AssertionOptions
          passkey: WalletPasskeySummary
          identity: WalletRecoveryIdentity
          walletText: string
      }
    | {
          kind: 'verify' | 'recover'
          options: AssertionOptions
          passkey: WalletPasskeySummary
          identity: WalletRecoveryIdentity
          deviceId?: string
      }

export function assertCurrentUser(userId?: string) {
    requireClientSigningFeature()
    const currentId = window.gon?.currentUser?.id
    if (!currentId || (userId && currentId !== userId)) {
        throw new Error('Your account changed. Reopen the wallet to continue.')
    }
    return currentId
}

export async function walletRequest<T>(
    path: string,
    body?: unknown,
    method?: 'DELETE',
): Promise<T> {
    assertCurrentUser()
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 30000)
    try {
        const response = await fetch(`${window.knovApiUrl}/api/v1/bsv/${path}`, {
            method: method || (body === undefined ? 'GET' : 'POST'),
            headers: {
                ...accessTokens(),
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            cache: 'no-store',
            signal: controller.signal,
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        })
        if (!response.ok) {
            // Never log the response or request; restore requests contain a share.
            const result = await response.json().catch(() => ({}))
            throw new Error(result.error || 'Wallet recovery request failed. Please try again.')
        }
        return response.json()
    } finally {
        clearTimeout(timeout)
    }
}

const passkeyRequest = <T>(path = '', body?: unknown) =>
    walletRequest<T>(`wallet_passkeys${path ? `/${path}` : ''}`, body)

export type PasskeyDevicePlatform = 'apple-mobile' | 'android' | 'other'

export function passkeyDevicePlatform(): PasskeyDevicePlatform {
    const native = window.KNOVIGATOR_NATIVE_APP_INFO?.platform
    if (native === 'ios') return 'apple-mobile'
    if (native === 'android') return 'android'
    const ua = navigator.userAgent
    if (/Android/i.test(ua)) return 'android'
    if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
        return 'apple-mobile'
    }
    return 'other'
}

// A backup only unlocks where its passkey's provider returns PRF. Cross-device (QR)
// sign-in from iOS to Google Password Manager returned none (2026-09-30), so phones hide
// the other platform's passkeys. Desktop browsers can reach both and keep them all.
export function passkeysUsableOnThisDevice(
    passkeys: WalletPasskeySummary[],
    platform: PasskeyDevicePlatform = passkeyDevicePlatform(),
): WalletPasskeySummary[] {
    const hidden = platform === 'apple-mobile' ? 'google' : platform === 'android' ? 'apple' : null
    return hidden ? passkeys.filter(passkey => passkey.provider?.ecosystem !== hidden) : passkeys
}

// The newest verified backup this device can unlock, preferring one that already holds
// the wallet's seed words. Older ones still recover; seed words are filled in from the server.
export function pickRestorePasskey(
    status: WalletPasskeyStatus,
    platform: PasskeyDevicePlatform = passkeyDevicePlatform(),
    // Passkeys already tried on this screen that couldn't unlock (not on this device).
    exclude: ReadonlySet<string> = new Set(),
): WalletPasskeySummary | null {
    const ready = passkeysUsableOnThisDevice(status.passkeys, platform)
        .filter(passkey => passkey.verified && passkey.envelope_present)
        .filter(passkey => !exclude.has(passkey.id))
        .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    const withSeedWords = status.root_identity_key
        ? ready.find(passkey => passkey.root_identity_key === status.root_identity_key)
        : undefined
    return withSeedWords || ready[0] || null
}

export async function loadWalletPasskeyStatus(): Promise<WalletPasskeyStatus> {
    const userId = assertCurrentUser()
    const result = await passkeyRequest<WalletPasskeyStatus>()
    assertCurrentUser(userId)
    if (result.identity.userId !== userId) throw new Error('Wallet recovery account mismatch.')
    return result
}

export async function discardPendingWalletPasskey(passkeyId: string): Promise<void> {
    await walletRequest(`wallet_passkeys/${encodeURIComponent(passkeyId)}`, undefined, 'DELETE')
}

// Wallets gained their seed words (plan B-R) after some passkeys and backup files
// were made, so a copy restored from one holds the funded keys without the words.
// While Treechat still holds the whole wallet, complete the copy from it: only when
// it is the same wallet, and never replacing words the copy already has.
export async function withSeedWordsFromServer(walletText: string): Promise<string> {
    if (walletRootIdentityPublicKey(walletText)) return walletText
    try {
        const local = JSON.parse(walletText)
        const server = await walletRequest<{ payPk?: string; ordPk?: string }>(
            'download-wallet?purpose=seed_words',
        )
        const localIdentity = getWalletIdentityInfo(local)
        const serverIdentity = getWalletIdentityInfo(server)
        if (
            !localIdentity ||
            localIdentity.paymentAddress !== serverIdentity?.paymentAddress ||
            localIdentity.ordinalsAddress !== serverIdentity?.ordinalsAddress ||
            !walletRootIdentityPublicKey(JSON.stringify(server))
        ) {
            return walletText
        }
        const { treechatRootMnemonic } = server as { treechatRootMnemonic: string }
        return JSON.stringify({ ...local, treechatRootMnemonic })
    } catch {
        // No server copy (or it can't be read): keep the copy as it is.
        return walletText
    }
}

async function currentWalletText(userId: string): Promise<string> {
    assertCurrentUser(userId)
    const local = await getClientShare(userId)
    if (!local) {
        throw new Error('Set up this wallet on this device, or recover an existing backup first.')
    }
    const server = await walletRequest<{ treechat_share: string; address: string }>(
        `server_share?device_id=${encodeURIComponent(local.deviceId)}`,
    )
    assertCurrentUser(userId)
    return withSeedWordsFromServer(
        await recoverLocalWalletTextWithShares(userId, server.treechat_share, local.clientShare),
    )
}

export async function prepareWalletPasskeyEnrollment(
    rootIdentityKey?: string | null,
): Promise<WalletPasskeyStep> {
    const userId = assertCurrentUser()
    const walletText = await currentWalletText(userId)
    // A backup made from a pre-root wallet copy would miss the seed words (plan B-R).
    if (rootIdentityKey && walletRootIdentityPublicKey(walletText) !== rootIdentityKey) {
        throw new Error(
            "This browser's wallet copy is missing your wallet seed words. Set this browser up again, then create the passkey.",
        )
    }
    const options = await passkeyRequest<EnrollmentOptions>('enrollment_options', {})
    assertCurrentUser(userId)
    if (options.identity.userId !== userId) throw new Error('Wallet recovery account mismatch.')
    return { kind: 'create', options, walletText }
}

export async function prepareWalletPasskeyAction(
    passkey: WalletPasskeySummary,
    identity: WalletRecoveryIdentity,
    kind: 'encrypt' | 'verify' | 'recover',
    walletText?: string,
): Promise<WalletPasskeyStep> {
    assertCurrentUser(identity.userId)
    const localWallet =
        kind === 'encrypt' ? walletText || (await currentWalletText(identity.userId)) : null
    const deviceId = kind === 'recover' ? crypto.randomUUID() : undefined
    const options = await passkeyRequest<AssertionOptions>('assertion_options', {
        passkey_id: passkey.id,
        purpose: kind === 'encrypt' ? 'enroll' : kind,
        ...(deviceId ? { device_id: deviceId } : {}),
    })
    assertCurrentUser(identity.userId)
    if (kind === 'encrypt') {
        return { kind, options, passkey, identity, walletText: localWallet! }
    }
    return { kind, options, passkey, identity, deviceId }
}

// Each invocation starts a WebAuthn operation synchronously from the user's
// click. Network preflight belongs in the prepare functions above.
export async function advanceWalletPasskeyStep(
    step: WalletPasskeyStep,
): Promise<{ next?: WalletPasskeyStep; recovered?: boolean }> {
    const identity = step.kind === 'create' ? step.options.identity : step.identity
    assertCurrentUser(identity.userId)
    if (step.kind === 'create') {
        const credential = await createWalletPasskey(step.options.public_key)
        assertCurrentUser(identity.userId)
        const registered = await passkeyRequest<{ passkey: WalletPasskeySummary }>('enroll', {
            ceremony_id: step.options.ceremony_id,
            credential,
        })
        return {
            next: await prepareWalletPasskeyAction(
                registered.passkey,
                identity,
                'encrypt',
                step.walletText,
            ),
        }
    }

    const { credential, prfOutput } = await useWalletPasskey(step.options.public_key)
    try {
        assertCurrentUser(identity.userId)
        if (credential.id !== step.passkey.credential_id) {
            throw new Error('A different passkey was selected. Please choose the matching passkey.')
        }
        const grant = await passkeyRequest<AssertionGrant>('assert', {
            ceremony_id: step.options.ceremony_id,
            credential,
        })
        assertCurrentUser(identity.userId)
        if (
            grant.identity.userId !== identity.userId ||
            grant.identity.paymentAddress !== identity.paymentAddress ||
            grant.identity.ordinalsAddress !== identity.ordinalsAddress
        ) {
            throw new Error('The wallet identity changed. Reopen the wallet to continue.')
        }
        if (step.kind === 'encrypt') {
            const envelope = await createWalletPasskeyEnvelope({
                walletText: step.walletText,
                prfOutput,
                credentialId: step.passkey.credential_id,
                prfSalt: step.options.public_key.extensions!.prf!.eval!.first!,
                identity,
            })
            const saved = await passkeyRequest<{
                passkey: WalletPasskeySummary
                verify?: { grant_id: string; envelope: unknown; proof_message: string }
            }>('envelope', {
                grant_id: grant.grant_id,
                envelope,
            })
            if (!saved.verify) {
                return { next: await prepareWalletPasskeyAction(saved.passkey, identity, 'verify') }
            }
            // Same approval verifies: open the backup Treechat saved with this approval's
            // PRF output, and prove both wallet keys from it (two taps, not three).
            const savedWallet = await openWalletPasskeyEnvelope(
                saved.verify.envelope as Parameters<typeof openWalletPasskeyEnvelope>[0],
                prfOutput,
                identity,
                step.passkey.credential_id,
            )
            assertCurrentUser(identity.userId)
            await passkeyRequest('complete', {
                grant_id: saved.verify.grant_id,
                proofs: signWalletRecoveryProof(savedWallet, saved.verify.proof_message),
            })
            return {}
        }

        const walletText = await openWalletPasskeyEnvelope(
            grant.envelope,
            prfOutput,
            identity,
            step.passkey.credential_id,
        )
        if (!grant.proof_message) throw new Error('Missing wallet verification challenge.')
        assertCurrentUser(identity.userId)
        if (step.kind === 'verify') {
            await passkeyRequest('complete', {
                grant_id: grant.grant_id,
                proofs: signWalletRecoveryProof(walletText, grant.proof_message),
            })
            return {}
        }

        // The ownership proof signs with the passkey's copy; the device keeps it
        // completed with the seed words when the passkey predates them.
        const prepared = await prepareRecoveredWalletForDevice(
            identity.userId,
            step.deviceId!,
            await withSeedWordsFromServer(walletText),
            identity,
        )
        const shareHash = new Uint8Array(
            await crypto.subtle.digest(
                'SHA-256',
                new TextEncoder().encode(prepared.shares.treechatShare),
            ),
        )
        const shareHashHex = Array.from(shareHash, byte => byte.toString(16).padStart(2, '0')).join(
            '',
        )
        const message = `${grant.proof_message}\nServer share SHA256: ${shareHashHex}`
        assertCurrentUser(identity.userId)
        await passkeyRequest('complete', {
            grant_id: grant.grant_id,
            proofs: signWalletRecoveryProof(walletText, message),
            treechat_share: prepared.shares.treechatShare,
        })
        const registered = await walletRequest<{ treechat_share: string }>(
            `server_share?device_id=${encodeURIComponent(step.deviceId!)}`,
        )
        if (registered.treechat_share !== prepared.shares.treechatShare) {
            throw new Error(
                'Device registration could not be verified. Your local wallet has not changed.',
            )
        }
        assertCurrentUser(identity.userId)
        await commitRecoveredWalletForDevice(prepared)
        return { recovered: true }
    } finally {
        prfOutput.fill(0)
    }
}
